// app/(app)/dashboard/index.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearActivityLogs,
  fetchActivityLogs,
} from "@/store/slices/activityLogSlice";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { router, useFocusEffect } from "expo-router";
import { useCallback } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { LineChart } from "react-native-chart-kit";

const { width } = Dimensions.get("window");

export default function DashboardScreen() {
  const { colors, spacing, mode } = useAppTheme();
  const dispatch = useAppDispatch();
  const { logs, loading, pagination } = useAppSelector(
    (state) => state.activityLogs,
  );

  // Fetch activity logs once on mount
  useFocusEffect(
    useCallback(() => {
      dispatch(fetchActivityLogs({ page: 1, limit: 20 }));
      return () => {
        dispatch(clearActivityLogs());
      };
    }, [dispatch]),
  );

  // Use the first 5 logs for recent activity (or all if less)
  const recentActivities = logs.slice(0, 5);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const getIconForAction = (action: string) => {
    if (action.includes("INVOICE")) return "document-text-outline";
    if (action.includes("CUSTOMER")) return "person-add-outline";
    if (action.includes("EMPLOYEE")) return "people-outline";
    if (action.includes("PRODUCT")) return "cube-outline";
    return "notifications-outline";
  };

  const handleViewMore = () => {
    router.push("/(app)/dashboard/activities");
  };

  // Mock data
  const stats = [
    {
      id: 1,
      title: "Total Employees",
      value: "24",
      icon: "people-outline",
      change: "+12%",
      color: "#3b82f6",
    },
    {
      id: 2,
      title: "Total Customers",
      value: "156",
      icon: "person-outline",
      change: "+8%",
      color: "#22c55e",
    },
    {
      id: 3,
      title: "Total Invoices",
      value: "89",
      icon: "document-text-outline",
      change: "+5%",
      color: "#f59e0b",
    },
    {
      id: 4,
      title: "Revenue (MTD)",
      value: "$12,345",
      icon: "cash-outline",
      change: "+18%",
      color: "#8b5cf6",
    },
  ];

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scrollContent: {
      padding: spacing.lg,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xl,
    },
    greeting: {
      fontSize: 20,
      fontWeight: "600",
      color: colors.text,
    },
    date: {
      fontSize: 14,
      color: colors.textLight,
      marginTop: 2,
    },
    statsGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      marginBottom: spacing.xl,
    },
    statCard: {
      width: (width - spacing.lg * 2 - spacing.md) / 2,
      backgroundColor: colors.background,
      borderRadius: 16,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: mode === "light" ? "#000" : "#fff",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 8,
      elevation: 2,
    },
    statIcon: {
      marginBottom: spacing.sm,
    },
    statValue: {
      fontSize: 28,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 4,
    },
    statTitle: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: 4,
    },
    statChange: {
      fontSize: 12,
      fontWeight: "500",
    },
    chartCard: {
      backgroundColor: colors.background,
      borderRadius: 16,
      padding: spacing.lg,
      marginBottom: spacing.xl,
      borderWidth: 1,
      borderColor: colors.border,
    },
    cardTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    activityCard: {
      backgroundColor: colors.background,
      borderRadius: 16,
      padding: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border,
    },
    activityItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    activityIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.primary + "15",
      justifyContent: "center",
      alignItems: "center",
      marginRight: spacing.md,
    },
    activityContent: {
      flex: 1,
    },
    activityAction: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.text,
    },
    activityUser: {
      fontSize: 12,
      color: colors.textLight,
      marginTop: 2,
    },
    activityTime: {
      fontSize: 12,
      color: colors.textLight,
    },
    activityHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    viewMoreText: {
      fontSize: 14,
      fontWeight: "500",
    },
  });

  // Simple mock chart placeholder (you can replace with a real chart library)
  const ChartPlaceholder = () => (
    <View
      style={{
        height: 200,
        backgroundColor: colors.border,
        borderRadius: 12,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <LineChart
        data={{
          labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
          datasets: [{ data: [20, 45, 28, 80, 99, 43] }],
        }}
        width={width - spacing.lg * 2 - 32}
        height={200}
        chartConfig={{
          backgroundColor: colors.background,
          backgroundGradientFrom: colors.background,
          backgroundGradientTo: colors.background,
          decimalPlaces: 0,
          color: (opacity = 1) => colors.primary,
          labelColor: (opacity = 1) => colors.textLight,
          style: { borderRadius: 16 },
        }}
        bezier
        style={{ borderRadius: 12 }}
      />
    </View>
  );

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.greeting}>Welcome back, 👋</Text>
          <Text style={styles.date}>
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Text>
        </View>
        <Ionicons name="notifications-outline" size={24} color={colors.text} />
      </View>

      {/* Stats Grid */}
      <View style={styles.statsGrid}>
        {stats.map((stat) => (
          <LinearGradient
            key={stat.id}
            colors={
              mode === "light" ? ["#ffffff", "#f8f9fa"] : ["#1e293b", "#0f172a"]
            }
            style={styles.statCard}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
          >
            <Ionicons
              name={stat.icon as any}
              size={32}
              color={stat.color}
              style={styles.statIcon}
            />
            <Text style={styles.statValue}>{stat.value}</Text>
            <Text style={styles.statTitle}>{stat.title}</Text>
            <Text
              style={[
                styles.statChange,
                { color: stat.change.includes("+") ? "#22c55e" : "#ef4444" },
              ]}
            >
              {stat.change} from last month
            </Text>
          </LinearGradient>
        ))}
      </View>

      {/* Chart Section */}
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Revenue Overview</Text>
        <ChartPlaceholder />
      </View>

      {/* Recent Activity */}
      <View style={styles.activityCard}>
        <View style={styles.activityHeader}>
          <Text style={styles.cardTitle}>Recent Activity</Text>
          {logs.length > 0 && (
            <TouchableOpacity onPress={handleViewMore}>
              <Text style={[styles.viewMoreText, { color: colors.primary }]}>
                View More
              </Text>
            </TouchableOpacity>
          )}
        </View>
        {loading && logs.length === 0 ? (
          <Text
            style={{
              color: colors.textLight,
              textAlign: "center",
              padding: spacing.lg,
            }}
          >
            Loading activities...
          </Text>
        ) : recentActivities.length === 0 ? (
          <Text
            style={{
              color: colors.textLight,
              textAlign: "center",
              padding: spacing.lg,
            }}
          >
            No recent activity
          </Text>
        ) : (
          recentActivities.map((activity) => (
            <View key={activity.id} style={styles.activityItem}>
              <View
                style={[
                  styles.activityIcon,
                  { backgroundColor: colors.primary + "15" },
                ]}
              >
                <Ionicons
                  name={getIconForAction(activity.action)}
                  size={20}
                  color={colors.primary}
                />
              </View>
              <View style={styles.activityContent}>
                <Text style={styles.activityAction}>
                  {activity.description}
                </Text>
                <Text style={styles.activityUser}>
                  {activity.user?.ownerName || activity.user?.email}
                </Text>
              </View>
              <Text style={styles.activityTime}>
                {formatTime(activity.createdAt)}
              </Text>
            </View>
          ))
        )}
      </View>
    </ScrollView>
  );
}
